/**
 * MetricsCards Component
 *
 * Dashboard summary cards showing key platform metrics.
 */

import React from 'react';
import { AdminMetrics } from '../hooks';

interface MetricsCardsProps {
  metrics: AdminMetrics;
}

export const MetricsCards: React.FC<MetricsCardsProps> = ({ metrics }) => {
  const cards = [
    {
      label: 'Total Users',
      value: metrics.users.total.toLocaleString(),
      subtext: `+${metrics.users.newThisMonth} this month`,
      icon: UsersIcon,
      color: 'blue',
    },
    {
      label: 'Active Listings',
      value: metrics.listings.active.toLocaleString(),
      subtext: `${metrics.listings.total} total`,
      icon: BuildingIcon,
      color: 'green',
    },
    {
      label: 'Bookings',
      value: metrics.bookings.confirmed.toLocaleString(),
      subtext: `${metrics.bookings.pending} pending`,
      icon: CalendarIcon,
      color: 'purple',
    },
    {
      label: 'Platform Revenue',
      value: `$${metrics.revenue.platformRevenue.toLocaleString()}`,
      subtext: `${metrics.revenue.growth >= 0 ? '+' : ''}${metrics.revenue.growth.toFixed(1)}% vs last month`,
      icon: DollarIcon,
      color: 'yellow',
      positive: metrics.revenue.growth > 0,
      negative: metrics.revenue.growth < 0,
    },
    {
      label: 'Pending Payouts',
      value: metrics.payouts.pending.toLocaleString(),
      subtext: `$${metrics.payouts.pendingAmount.toLocaleString()} total`,
      icon: CashIcon,
      color: 'red',
      alert: metrics.payouts.pending > 0,
    },
  ];

  const colorClasses: Record<string, { icon: string; bg: string }> = {
    blue: { icon: 'text-blue-500', bg: 'bg-blue-50' },
    green: { icon: 'text-green-500', bg: 'bg-green-50' },
    purple: { icon: 'text-purple-500', bg: 'bg-purple-50' },
    yellow: { icon: 'text-yellow-600', bg: 'bg-yellow-50' },
    red: { icon: 'text-red-500', bg: 'bg-red-50' },
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className={`bg-white rounded-xl shadow-sm p-5 border ${
            card.alert ? 'ring-2 ring-red-400' : 'border-gray-100'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-500 font-medium">{card.label}</span>
            <div className={`p-2 rounded-lg ${colorClasses[card.color].bg}`}>
              <card.icon className={`w-5 h-5 ${colorClasses[card.color].icon}`} />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{card.value}</p>
          <p
            className={`text-xs mt-1 ${
              card.positive
                ? 'text-green-600'
                : card.negative
                ? 'text-red-600'
                : 'text-gray-500'
            }`}
          >
            {card.subtext}
          </p>
        </div>
      ))}
    </div>
  );
};

// Icon components
const UsersIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const BuildingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const DollarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CashIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

export default MetricsCards;
